import React from 'react';
import { Column } from 'react-table';
import SessionTable, {
  ResponseGuard,
  SessionTableInstanceProps
} from '../index';
import {
  DirectCell,
  CommentCell,
  SurveyTypeCell,
  YesNoCell,
  RESPONSE_NONE,
  CellFunction
} from '../../cells';
import Tooltip from '../../../tooltip';
import Icon from '../../../../atoms/icon';

import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import { ISurveyQual } from '../../../../../libs/apps/surveyqual';
import { GetCurrentTZ } from '../../../../../libs/time';
import moment from 'moment-timezone';

const CURRENT_TZ = GetCurrentTZ();

const columns: Column<ISurveyQual>[] = [
  {
    id: 'ready',
    Header: () => (
      <Tooltip content="Thank you for answering the texts/calls! Do you have some time to rate them?">
        <span>Ready to Answer?</span>
        <Icon className="ml-2" icon={faQuestionCircle} />
      </Tooltip>
    ),
    Cell: ResponseGuard(YesNoCell('ready') as CellFunction<ISurveyQual>)
  },
  {
    id: 'communication',
    Header: () => (
      <Tooltip content="These texts/calls have improved your communication. Rate between 1 - 9. (1 = strongly disagree, 5 = neutral, 9 = strongly agree)">
        <span>Communication</span>
        <Icon className="ml-2" icon={faQuestionCircle} />
      </Tooltip>
    ),
    Cell: ResponseGuard(
      DirectCell('communication') as CellFunction<ISurveyQual>
    )
  },
  {
    id: 'healthLiteracy',
    Header: () => (
      <Tooltip content="These texts/calls have helped me understand my condition better. Rate between 1-9 (1 = strongly disagree, 5 = neutral, 9 = strongly agree)">
        <span>Health Literacy</span>
        <Icon className="ml-2" icon={faQuestionCircle} />
      </Tooltip>
    ),
    Cell: ResponseGuard(
      DirectCell('healthLiteracy') as CellFunction<ISurveyQual>
    )
  },
  {
    id: 'frequencyRating',
    Header: () => (
      <Tooltip content="Texts/calls are sent at just the right frequency. Rate between 1 - 9. (1 = strongly disagree, 5 = neutral, 9 = strongly agree)">
        <span>Right Frequency</span>
        <Icon className="ml-2" icon={faQuestionCircle} />
      </Tooltip>
    ),
    Cell: ResponseGuard(
      DirectCell('frequencyRating') as CellFunction<ISurveyQual>
    )
  },
  {
    id: 'frequency',
    Header: () => (
      <Tooltip content="Help us improve the message frequency. Why did you rate the message frequency this? Reply 1 - 9. (1 = too few texts, 5 = just right, 9 = too many texts)">
        <span>Frequency</span>
        <Icon className="ml-2" icon={faQuestionCircle} />
      </Tooltip>
    ),
    Cell: ResponseGuard(DirectCell('frequency') as CellFunction<ISurveyQual>)
  },
  {
    id: 'rate',
    Header: () => (
      <Tooltip content="You are getting the best possible care. Rate between 1 - 9. (1 = strongly disagree, 5 = neutral, 9 = strongly agree)">
        <span>Care Satisfaction</span>
        <Icon className="ml-2" icon={faQuestionCircle} />
      </Tooltip>
    ),
    Cell: ResponseGuard(DirectCell('rate') as CellFunction<ISurveyQual>)
  },
  {
    id: 'nps',
    Header: () => (
      <Tooltip content="I would recommend this to a friend or family member. Rate between 0-10 (0 = not at all likely, 10 = extremely likely)">
        <span>NPS Survey</span>
        <Icon className="ml-2" icon={faQuestionCircle} />
      </Tooltip>
    ),
    Cell: ResponseGuard(DirectCell('nps') as CellFunction<ISurveyQual>)
  },
  {
    id: 'improvementComment',
    Header: 'Improvement Comment',
    Cell: ResponseGuard(CommentCell('improvementComment')),
    maxWidth: 10
  },
  {
    id: 'positiveComment',
    Header: 'Positive Comment',
    Cell: ResponseGuard(CommentCell('positiveComment'))
  },
  {
    Header: 'Responded At',
    accessor: 'respondedAt',
    Cell: ({ cell: { value }, row: { original: session } }) => {
      if (!value) {
        return RESPONSE_NONE;
      }
      const tz = session?.schedule?.tz || session?.tz || CURRENT_TZ;
      const time = moment.tz(value as Date, tz);
      return <span>{time.format('MM/DD/YY hh:mm A zz')}</span>;
    }
  },
  {
    Header: 'Msg Time',
    accessor: 'time',
    Cell: ({ cell: { value }, row: { original: session } }) => {
      const tz = session?.schedule?.tz || session?.tz || CURRENT_TZ;
      const time = moment.tz(value as Date, tz);
      return <span>{time.format('MM/DD/YY hh:mm A zz')}</span>;
    }
  },
  {
    Header: 'Msg Type',
    id: 'callIds',
    Cell: SurveyTypeCell()
  }
];

/**
 * We do not need the extra controls for including future or canceled
 * because those are not relevant to quality surveys.
 *
 * Disable the standard columns to improve the table UI/UX
 */
export const SurveyQualTable: React.FC<SessionTableInstanceProps> = (
  props
): JSX.Element => {
  return (
    <SessionTable<ISurveyQual>
      appKey="surveyqual"
      url="/survey/data_qual_dashboard/"
      columns={columns}
      {...props}
      disableControls
      disableTypeColumn
      disableDefaultColumns
    />
  );
};

export default SurveyQualTable;
